import { SocialSharingActions } from '@rocco/ui/social-sharing/components/SocialSharingActions';
import { ClientOnly } from 'remix-utils';

import type { Platform } from '@rocco/ui/social-sharing/utils/platform';
import type { DisplayMode } from '@rocco/types/enum/display-mode';
import {
    shareNative,
    shareContent,
    type ShareOptions,
    isNativeShareAvailable,
} from '../utils/sharing';
import { useTracker } from '@archipro-website/tracker';
import { capitalizeFirstLetter } from '../../../utils/stringHelper';
import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { uiStateAtomSocialSharingModalIsOpened } from '@rocco/ui/social-sharing/components/SocialSharingModal';
import { shareTargetAtom } from '../state/share-target';
interface SocialShareInitializerProps {
    displayMode: DisplayMode;
}

export const SocialShareInitializer = ({
    displayMode,
}: SocialShareInitializerProps) => {
    const tracker = useTracker();
    const shareTarget = useAtomValue(shareTargetAtom);
    const setShareTarget = useSetAtom(shareTargetAtom);
    const shareModalOpen = useAtomValue(uiStateAtomSocialSharingModalIsOpened);

    // Clear any per-item share override once the modal closes.
    useEffect(() => {
        if (!shareModalOpen) setShareTarget(null);
    }, [shareModalOpen, setShareTarget]);

    const onShare = (platform: Platform) => {
        // Get Open Graph metadata from meta tags
        const getMetaContent = (property: string): string => {
            const metaTag =
                document.querySelector(`meta[property="${property}"]`) ||
                document.querySelector(`meta[name="${property}"]`);
            return metaTag ? metaTag.getAttribute('content') || '' : '';
        };

        // A per-item override (e.g. a listing tile) wins; otherwise share the
        // current page via Open Graph metadata, falling back to the page URL.
        const opts: ShareOptions = shareTarget ?? {
            url: getMetaContent('og:url') || window.location.href,
            title: getMetaContent('og:title') || window.document.title,
            description:
                getMetaContent('og:description') ||
                getMetaContent('description'),
        };

        if (displayMode === 'mobile') {
            // Try native share if available, otherwise fall back to copy link
            if (isNativeShareAvailable()) {
                shareNative(opts);
                tracker.log('ShareSubmit', {
                    url: new URL(window.location.href),
                    data: {
                        ExtraData: 'NativeShare',
                    },
                });
            } else {
                // Fall back to copy link if native sharing is not supported
                shareContent('copy', opts);
            }
        } else {
            shareContent(platform, opts);
        }
    };

    return (
        <ClientOnly>
            {() => (
                <>
                    <SocialSharingActions
                        displayMode={displayMode}
                        onShare={onShare}
                        trackingConfig={{
                            onOpen: () => {
                                tracker.log('ShareReveal', {
                                    url: new URL(window.location.href),
                                });
                            },
                            onShare: (platform) => {
                                tracker.log('ShareSubmit', {
                                    url: new URL(window.location.href),
                                    data: {
                                        ExtraData:
                                            capitalizeFirstLetter(platform),
                                    },
                                });
                            },
                        }}
                    />
                </>
            )}
        </ClientOnly>
    );
};
